import {
  useRouter,
  useRoute,
  type Router,
  type RouteLocationNormalizedLoaded,
} from 'vue-router';
import { useUserStore } from '@/store';

/**
 * 构建带 workspace_uuid 前缀的路径
 * @param path 原始路径
 * @param workspaceUuid workspace UUID
 */
export function buildWorkspacePath(
  path: string,
  workspaceUuid?: string
): string {
  // 如果是外部链接，直接返回
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // 如果路径已经包含 /space/，直接返回
  if (path.startsWith('/space/')) {
    return path;
  }

  // 如果没有 workspaceUuid，直接返回原路径
  if (!workspaceUuid) {
    return path;
  }

  // 确保路径以 / 开头
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `/space/${workspaceUuid}${cleanPath}`;
}

/**
 * 获取带 workspace_uuid 的完整路径（在 Vue setup 中使用）
 */
export function getPathWithWorkspace(path: string): string {
  const userStore = useUserStore();
  const route = useRoute();

  // 从当前路由或 store 获取 workspace_uuid
  const workspaceUuid =
    (route.params.workspace_uuid as string) || userStore.workspaceUuid;

  return buildWorkspacePath(path, workspaceUuid);
}

/**
 * 导航到带 workspace_uuid 的路径（在 Vue setup 中使用）
 */
export function navigateWithWorkspace(path: string) {
  const router = useRouter();
  const route = useRoute();
  const fullPath = getPathWithWorkspace(path);

  router.push({
    path: fullPath,
    query: route.query,
  });
}

/**
 * 创建一个带 workspace 前缀的 push 方法
 * 用于替代 router.push，自动添加 workspace_uuid 前缀
 */
export function createWorkspaceRouter(
  router: Router,
  route: RouteLocationNormalizedLoaded,
  userStore: ReturnType<typeof useUserStore>
) {
  const getWorkspaceUuid = () =>
    (route.params.workspace_uuid as string) || userStore.workspaceUuid;

  return {
    push(path: string) {
      const fullPath = buildWorkspacePath(path, getWorkspaceUuid());
      return router.push(fullPath);
    },
    replace(path: string) {
      const fullPath = buildWorkspacePath(path, getWorkspaceUuid());
      return router.replace(fullPath);
    },
  };
}

export function getWorkspaceFromUrl() {
  const path = window.location.pathname;
  const match = path.match(/^\/space\/([^/]+)/);
  return match ? match[1] : undefined;
}
